import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { NgOptimizedImage, UpperCasePipe } from '@angular/common';

import { ActivityMonitorComponent } from '@app/shared/layout/header/toolbars/activity-monitor/activity-monitor.component';
import { ActivityPanelStateService } from '@app/shared/layout/header/toolbars/activity-monitor/activity-panel-state.service';
import { Bind } from 'primeng/bind';
import { Drawer } from 'primeng/drawer';
import { FeedbackPanelService } from '@app/shared/layout/feedback-widget/feedback-panel.service';
import { MenuIconsComponent } from '@app/shared/icon-svg/menu-icons/menu-icons.component';
import { Router } from '@angular/router';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    host: {
        '(document:click)': 'onDocumentClick($event)',
        '(document:keydown.escape)': 'onEscapeKey()'
    },
    selector: 'ic-toolbar-user',
    templateUrl: './toolbar-user.component.html',
    styleUrls: ['./toolbar-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgOptimizedImage, Tooltip, MenuIconsComponent, Bind, Drawer, ActivityMonitorComponent, UpperCasePipe, TranslatePipe]
})
export class ToolbarUserComponent {
    private router = inject(Router);

    private activityPanelState = inject(ActivityPanelStateService);
    private feedbackPanel = inject(FeedbackPanelService);

    protected readonly feedbackEnabled = this.feedbackPanel.enabled;
    protected readonly feedbackOpen = this.feedbackPanel.isOpen;

    protected readonly showActivityPanel = signal(false);
    protected readonly showUserMenu = signal(false);
    /** Blue dot on the clock icon (AB#20613). */
    protected readonly hasNewActivity = this.activityPanelState.hasNewActivity;

    private readonly activityTrigger = viewChild<ElementRef<HTMLElement>>('activityTrigger');
    private readonly userMenuTrigger = viewChild<ElementRef<HTMLElement>>('userMenuTrigger');
    private readonly userMenuPanel = viewChild<ElementRef<HTMLElement>>('userMenuPanel');

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    toggleUserMenu(event: Event) {
        event.stopPropagation();
        this.showUserMenu.update(open => !open);
    }

    onDocumentClick(event: MouseEvent) {
        this.closeActivityPanelOnOutsideClick(event);

        if (!this.showUserMenu()) {
            return;
        }

        const target = event.target as Node;
        const clickedInsideTrigger = this.userMenuTrigger()?.nativeElement.contains(target);
        const clickedInsidePanel = this.userMenuPanel()?.nativeElement.contains(target);

        if (!clickedInsideTrigger && !clickedInsidePanel) {
            this.showUserMenu.set(false);
        }
    }

    onEscapeKey() {
        this.showUserMenu.set(false);
    }

    onLogoutClick() {
        this.showUserMenu.set(false);
        this.router.navigate(['/logout']);
    }

    onProfileClicked() {
        this.showUserMenu.set(false);
        this.router.navigate(['/user/user-profile']);
    }

    /** The Activity panel takes the same spot, so it shuts on the way; a click outside it would shut it anyway. */
    onFeedbackClick() {
        this.showActivityPanel.set(false);
        this.feedbackPanel.toggle();
    }

    onShutdownClick() {
        this.router.navigate(['/shutdown']);
    }

    /** The feedback drawer takes the same spot, so it shuts on the way in; its draft is kept, as Cancel keeps it. */
    toggleActivityMonitor() {
        this.showActivityPanel.update(open => !open);

        if (this.showActivityPanel()) {
            this.feedbackPanel.close();
        }
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    /**
     * The Figma panel has no close button (AB#20613): a click anywhere outside it closes it. Clicks on the clock icon
     * are left to toggleActivityMonitor, or this would close the panel and the toggle would reopen it.
     */
    private closeActivityPanelOnOutsideClick(event: MouseEvent) {
        if (!this.showActivityPanel()) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target?.isConnected) {
            // The click removed its own element (e.g. a menu item that closed its menu); it came from inside.
            return;
        }

        const clickedTrigger = this.activityTrigger()?.nativeElement.contains(target);
        const clickedPanel = !!target.closest('.toro-notification-sidebar');

        if (!clickedTrigger && !clickedPanel) {
            this.showActivityPanel.set(false);
        }
    }
}
